import React, { useRef, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import { Button, Input, Space, Table } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import "./modal_po_data_supplier.css";
import axios from 'axios';
import useSWR from 'swr';

interface DataType {
    key: string;
    supplierCode: string;
    supplierName: string;
    address: string;
    status: string;
    discount: number;
    phone: string;
}

type DataIndex = keyof DataType;
const fetcher = (url: string) => axios.get(url).then(res => res.data);

interface ModalPoDataSupplierProps {
    onSupplierSelect: (supplier: DataType) => void;
}

const ModalPoDataSupplier: React.FC<ModalPoDataSupplierProps> = ({ onSupplierSelect }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const { data: supplierData, error: supplierError, isLoading: supplierIsLoading } = useSWR<DataType[]>(`${process.env.REACT_APP_BACKEND_URL}/api/po/suppliers`, fetcher);

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns: TableColumnsType<DataType> = [
        {
            title: 'Supplier Code',
            dataIndex: 'supplierCode',
            key: 'supplierCode',
            ...getColumnSearchProps('supplierCode'),
            sorter: (a, b) => a.supplierCode.localeCompare(b.supplierCode),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Supplier Name',
            dataIndex: 'supplierName',
            key: 'supplierName',
            ...getColumnSearchProps('supplierName'),
            sorter: (a, b) => a.supplierName.localeCompare(b.supplierName),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Address',
            dataIndex: 'address',
            key: 'address',
            ...getColumnSearchProps('address'),
            sorter: (a, b) => a.address.localeCompare(b.address),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',

            ...getColumnSearchProps('status'),
            sorter: (a, b) => a.status.localeCompare(b.status),
            sortDirections: ['descend', 'ascend'],
            render: (status: string) => {
                if (status === '1') return 'Active';
                if (status === '2') return 'Deactive';
                return status;
            },

        },
    ];

    if (supplierError) return <div>Gagal memuat data supplier. Silakan coba lagi.</div>;

    return (

        <div id="ilqt" style={transformStyle}>
            <div className="gjs-grid-row" id="i0zc">
                <div className="gjs-grid-column" id="ivne">
                    <Table<DataType>
                        columns={columns}
                        dataSource={supplierData}
                        loading={supplierIsLoading}
                        onRow={(record) => ({
                            onDoubleClick: () => {
                                onSupplierSelect(record);
                            },
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default ModalPoDataSupplier;